import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Card,
  CardBody,
  Divider,
  Flex,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React, { useEffect, useState } from 'react';

import {
  BaseThreadFragmentFragmentGQLType,
  ThreadActionGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { CommentBox } from './CommentBox';
import {
  CommentEditor,
  CommentEditorCancelButton,
  CommentEditorCreateButton,
  CommentEditorInput,
} from './CommentEditor';
import { ExternalPartyAlert } from './ExternalPartyAlert';
import { useCreateCommentMutation } from './mutations/CreateComment.gql.generated';
import { PinThreadButton } from './PinThreadButton';
import { useGetThreadByIdQuery } from './queries/GetThreadById.gql.generated';
import { ResolveThreadButton } from './ResolveThreadButton';
import { ErrorBoundary } from '~/contexts/MonitoringProvider';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { UserDetail } from '~/packages/common-frontend/components/UserDetail';

// Hooks
type UseThreadQueryWithExternalReadyStateSyncOptions = {
  queryArgs: Parameters<typeof useGetThreadByIdQuery>[0];
  onMount?: () => void;
  onReady?: () => void;
};
/**
 * Related to scrolling to bottom. This wrapper around `useGetThreadByIdQuery`:
 *
 * 1. Calls `onMount` when this hook is mounted
 * 2. Calls `onReady` when the query completes
 *
 * This is used to sync query loading state to the parent `<ThreadFeed>` and `<EventFeed>`
 * components. The parent components need to delay the "scroll to bottom" action long enough for
 * their children to load and render themselves. Calling `onMount` tells the parent that there's a
 * component that it should wait for. Calling `onReady` tells the parent that this particular child
 * has finished loading. When the parent eventually runs out of children to wait on, it will finally
 * scroll the feed container to the bottom via the `useScrollToAfterAllLoaded` hook. See the JSDoc
 * in `useScrollToAfterAllLoaded.ts` for more information.
 */
const useThreadQueryWithExternalReadyStateSync = ({
  queryArgs,
  onReady,
  onMount,
}: UseThreadQueryWithExternalReadyStateSyncOptions) => {
  const [isReady, setIsReady] = useState(false);
  useEffect(() => {
    // Thread was mounted, so it's assumed to be in loading state
    if (!isReady) onMount?.();
  }, [isReady, onMount]);

  const threadQuery = useGetThreadByIdQuery(queryArgs);
  useEffect(() => {
    // Thread finished loading
    if (threadQuery.data && !isReady) {
      setIsReady(true);
      onReady?.();
    }
  }, [isReady, onReady, threadQuery.data]);

  return threadQuery;
};

// Fragments
type PinnedWrapperProps = {
  'data-testid'?: string;
  thread: BaseThreadFragmentFragmentGQLType;
  children: React.ReactNode;
};
const PinnedWrapper = ({
  'data-testid': testId = 'PinnedWrapper',
  children,
  thread,
}: PinnedWrapperProps) => {
  const pinnedBgColor = useColorModeValue('gray.50', 'gray.700');
  if (!thread.pinned) return <>{children}</>;
  return (
    <Card
      data-testid={testId}
      variant="outline"
      background={pinnedBgColor}
      width="full"
    >
      <CardBody
        display="flex"
        flexDirection="column"
        gap="0.75rem"
        padding="0.75rem"
      >
        <Typography variant="detail" fontWeight="semibold" trim>
          Pinned
          {thread.latestActionLog?.actionType === 'pin' && (
            <span>
              {' '}
              by{' '}
              <UserDetail variant="name" user={thread.latestActionLog.user} />
            </span>
          )}
        </Typography>
        {children}
      </CardBody>
    </Card>
  );
};

type ThreadBaseProps = {
  children?: React.ReactNode;
  loadingFallback?: React.ReactNode;
  threadId: string;
  onCreateComment?: (params: {
    commentId: string;
    commentText: string;
  }) => Promise<void>;
  onThreadAction?: (action: ThreadActionGQLType) => void;
  onMount?: () => void;
  onReady?: () => void;
};
const ThreadBase = ({
  children,
  loadingFallback = null,
  threadId,
  onCreateComment,
  onThreadAction,
  onMount,
  onReady,
}: ThreadBaseProps) => {
  const toast = useToast();
  const showAllDisclosure = useDisclosure();
  const [createComment] = useCreateCommentMutation();
  const threadQuery = useThreadQueryWithExternalReadyStateSync({
    onReady,
    onMount,
    queryArgs: {
      variables: { threadId },
      // Still favor cache, but always query for updated thread data in parallel. This balances the
      // speedy cache lookup with the need to keep thread data as up-to-date as possible.
      fetchPolicy: 'cache-and-network',
    },
  });
  const thread = threadQuery.data?.thread;
  if (threadQuery.error) throw threadQuery.error;
  if (!thread) return <>{loadingFallback}</>;

  const [firstComment, ...restComments] = thread.comments;
  const lastTwoComments = restComments.slice(-2);
  const visibleComments = showAllDisclosure.isOpen
    ? restComments
    : lastTwoComments;

  return (
    <PinnedWrapper thread={thread}>
      <Card variant="outline" width="100%">
        <CardBody
          display="flex"
          flexDirection="column"
          gap="0.75rem"
          padding="0.75rem"
        >
          <ExternalPartyAlert thread={thread} />
          {children}

          <CommentBox
            comment={firstComment}
            actions={
              <>
                <PinThreadButton
                  thread={thread}
                  onClick={async (threadAction) => {
                    await onThreadAction?.(threadAction);
                    await threadQuery.refetch();
                  }}
                />
                <ResolveThreadButton
                  thread={thread}
                  onClick={async (threadAction) => {
                    await onThreadAction?.(threadAction);
                    await threadQuery.refetch();
                  }}
                />
              </>
            }
          />

          <Flex direction="column" gap="0.75rem">
            {visibleComments.length > 0 && (
              <>
                <Divider />
                {restComments.length !== lastTwoComments.length &&
                  !showAllDisclosure.isOpen && (
                    <Button
                      variant="secondary-muted"
                      size="sm"
                      onClick={showAllDisclosure.onOpen}
                    >
                      Show Older Comments
                    </Button>
                  )}
                {visibleComments.map((comment) => (
                  <CommentBox key={comment.id} comment={comment} />
                ))}
              </>
            )}

            {!thread.resolved && (
              <>
                <Divider />
                <CommentEditor
                  onCreate={async (comment) => {
                    try {
                      const result = await createComment({
                        variables: {
                          comment,
                          threadId,
                        },
                      });
                      if (!result.data) throw Error('Failed to create comment');

                      // Some consumers of <Thread /> need to run GQL mutations to "register" a
                      // comment with a thread. Awaiting on the consumer-provided callback here
                      // before refetching threads gives us a way to defer refetch until all
                      // consumer mutations are resoled.
                      await onCreateComment?.({
                        commentId: result.data.commentId,
                        commentText: comment,
                      });

                      await threadQuery.refetch();
                    } catch (error) {
                      Sentry.captureException(error);
                      toast({
                        title: 'Failed to create comment',
                        status: 'error',
                        position: 'top',
                      });
                    }
                  }}
                  right={
                    <>
                      <CommentEditorCancelButton />
                      <CommentEditorCreateButton />
                    </>
                  }
                >
                  <CommentEditorInput isEditable />
                </CommentEditor>
              </>
            )}
          </Flex>
        </CardBody>
      </Card>
    </PinnedWrapper>
  );
};

// Exports
type ThreadProps = ThreadBaseProps;
export const Thread = (props: ThreadProps) => {
  return (
    <ErrorBoundary
      fallback={
        <Alert size="sm" status="error">
          <AlertIcon />
          <AlertDescription>Failed to load thread</AlertDescription>
        </Alert>
      }
    >
      <ThreadBase {...props} />
    </ErrorBoundary>
  );
};
